import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Receipt, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { log } from "@/lib/logger";

const fmtEuro = (cents, currency = "eur") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format((cents || 0) / 100);
const fmtDate = (unix) => (unix ? new Date(unix * 1000).toLocaleDateString("fr-FR") : "—");
const STATUS_LABEL = { paid: "Payée", open: "En attente", uncollectible: "Impayée", void: "Annulée", draft: "Brouillon" };

export default function InvoicesDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/invoices");
      setItems(data?.items || []);
    } catch (err) {
      log.error("billing invoices", err);
      toast.error("Chargement impossible");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] flex flex-col p-0" data-testid="invoices-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#E1E9F0] shrink-0">
          <DialogTitle className="font-serif text-2xl text-[#1E3A5F] flex items-center gap-2">
            <Receipt size={22} weight="duotone" className="text-[#3A8FA5]" /> Mes factures
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5F758F]">
            Historique des 12 dernières factures — téléchargez le PDF en un clic.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <div className="text-sm text-[#5F758F] py-6 text-center">Chargement…</div>}
          {!loading && items.length === 0 && (
            <div className="text-sm text-[#5F758F] italic py-8 text-center border border-dashed border-[#E1E9F0] rounded-md">
              Aucune facture pour l'instant
            </div>
          )}
          {!loading && items.length > 0 && (
            <ul className="divide-y divide-[#E1E9F0] border border-[#E1E9F0] rounded-md">
              {items.map((inv) => (
                <li key={inv.id} className="p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-[#1E3A5F] font-medium">
                      {inv.number || inv.id.slice(0, 12)}
                      <span className="ml-2 text-xs text-[#5F758F]">{fmtDate(inv.created)}</span>
                    </div>
                    <div className="text-xs text-[#5F758F]">
                      {fmtEuro(inv.amount_paid, inv.currency)} · {STATUS_LABEL[inv.status] || inv.status}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {inv.invoice_pdf && (
                      <a
                        href={inv.invoice_pdf}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#3A8FA5] hover:text-[#164E5F] px-2 py-1 rounded hover:bg-[#F4F7FB] transition-colors"
                        data-testid={`invoice-pdf-${inv.id}`}
                      >
                        <DownloadSimple size={14} /> PDF
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
